import moment from "moment";
import _uniqBy from "lodash/uniqBy";
import Store from "../SessionStorage";
import { getDateTimeFormat } from "./dateUtil";

const DateTimeFormat = getDateTimeFormat();

export const dateTimeFormat = DateTimeFormat;

export const thirtySixMonthBeforeNow = moment().subtract(36, "month").toDate();
export const eighteenYearsBeforeNow = moment().subtract(18, "year").toDate();
export const twoYearsBeforeNow = moment().subtract(2, "year").toDate();

export const sessionStorageAction = {
  request: {
    simulation: "@@REQUEST/SIMULATION",
    exactPrices: "@@REQUEST/EXACT_PRICES",
  },
  response: {
    simulation: "@@RESPONSE/SIMULATION",
    exactPrices: "@@RESPONSE/EXACT_PRICES",
  },
  data: {
    contact: "@@DATA/CONTACT",
    city: "@@DATA/CITY",
    vehicle: "@@DATA/VEHICLE",
    driver: "@@DATA/DRIVER",
    quotation: "@@DATA/QUOTATION",
    session: "@@DATA/SESSION",
    sessionRecap: "@@DATA/SESSION_RECAP",
  },
  ui: {
    vehicle: "@@UI/VEHICLE",
    insurance: "@@UI/INSURANCE",
    job: "@@UI/JOB",
    city: "@@UI/CITY",
  },
  cache: {
    showBankForm: "@@CACHE/SHOW_BANK_FORM",
    simulation: {
      vehicle: "@@CACHE/SIMULATION/VEHICLE",
      isEligibleVehicle: "@@CACHE/SIMULATION/IS_ELIGIBLE_VEHICLE",
      isEligibleLocation: "@@CACHE/SIMULATION/IS_ELIGIBLE_LOCATION",
      isEligibleCRM: "@@CACHE/SIMULATION/IS_ELIGIBLE_CRM",
      pricingBarValues: "@@CACHE/SIMULATION/PRICING_BAR_VALUES",
      withFranchise: "@@CACHE/SIMULATION/WITH_FRANCHISE",
      withoutFranchise: "@@CACHE/SIMULATION/WITHOUT_FRANCHISE",
    },
    quotations: {
      withFranchise: "@@CACHE/QUOTATIONS/WITH_FRANCHISE",
      withoutFranchise: "@@CACHE/QUOTATIONS/WITHOUT_FRANCHISE",
    },
    devis: {
      devisVehicle: "@@CACHE/DEVIS/VEHICLE",
    },
    leaving: {
      isAlreadyAsked: "@@CACHE/LEAVING/IS_ALREADY_ASKED",
      isAlreadyAskedSuccess: "@@CACHE/LEAVING/IS_ALREADY_ASKED_SUCCESS",
    },
  },
  dashboard: {
    accessToken: "@@DASHBOARD/ACCESS_TOKEN",
    client: {
      firstName: "@@DASHBOARD/CLIENT/FIRST_NAME",
      lastName: "@@DASHBOARD/CLIENT/LAST_NAME",
      email: "@@DASHBOARD/CLIENT/EMAIL",
      numClient: "@@DASHBOARD/CLIENT/NUM_CLIENT",
      numContract: "@@DASHBOARD/CLIENT/NUM_CONTRACT",
      numAltima: "@@DASHBOARD/CLIENT/NUM_ALTIMA",
      codeProduit: "@@DASHBOARD/CLIENT/CODE_PRODUIT",
    },
    vehicle: {
      immatriculation: "@@DASHBOARD/VEHICLE/IMMATRICULATION",
      codeSRA: "@@DASHBOARD/VEHICLE/CODE_SRA",
      modelText: "@@DASHBOARD/VEHICLE/MODEL_TEXT",
      brandText: "@@DASHBOARD/VEHICLE/BRAND_TEXT",
      version: "@@DASHBOARD/VEHICLE/VERSION",
    },
  },
  management: {
    user: {
      isUserAlreadyExist: "@@MANAGEMENT/USER/IS_USER_ALREADY_EXIST",
    },
  },
  validBrowsers: "@@VALID_BROWSERS",
};

// Generate unique IDs for use as pseudo-private/protected names.
// Similar in concept to
// <http://wiki.ecmascript.org/doku.php?id=strawman:names>.
//
// The goals of this function are twofold:
//
// * Provide a way to generate a string guaranteed to be unique when compared
//   to other strings generated by this function.
// * Make the string complex enough that it is highly unlikely to be
//   accidentally duplicated by hand (this is key if you're using `ID`
//   as a private/protected name on an object).
//
// Use:
//
//     var privateName = ID();
//     var o = { 'public': 'foo' };
//     o[privateName] = 'bar';
export const generateRandomID = () =>
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  `_${Math.random().toString(36).substr(2, 9)}`;

// Return all data of simulation parts
export const getSimulationDataSummary = () => {
  const dataVehicle = Store.get(sessionStorageAction.cache.simulation.vehicle);
  const { zipCode } = Store.get(sessionStorageAction.data.city);
  const {
    optionName,
    withOptionalFranchise,
    damageFranchise,
    specificFranchise,
    driveModeDto: { pricePerMinute },
    parkModeDto: { pricePerMonth },
  } = Store.get(sessionStorageAction.data.quotation);
  const { finalPricing, unitTime, valueTime } = Store.get(
    sessionStorageAction.cache.simulation.pricingBarValues
  );
  return Object.assign(
    {},
    dataVehicle,
    { dept: zipCode.substring(0, 2) },
    { optionName: optionName.replace(/\s/g, "_") },
    { withOptionalFranchise: withOptionalFranchise ? "Oui" : "Non" },
    { damageFranchise },
    { specificFranchise },
    { pricePerMinute },
    { pricePerMonth },
    { finalPricing },
    { unitTime },
    { valueTime }
  );
};

/**
 * Return a filtered list by key with unique objects and with asc ordering
 *
 * @param array
 * @param key
 *
 * @returns {*}
 */
export const filteredList = (array, key = "label") => {
  const ascFilteredList = _.orderBy(array, [key], ["asc"]);
  const finalFilteredList = _uniqBy(ascFilteredList, key);
  return finalFilteredList;
};

/**
 * Format YYYYMMDD Date to DD/MM/YYYY
 *
 * @param date
 *
 * @returns {*}
 */
export const formattedDate = (date) =>
  `${date.slice(6, 8)}/${date.slice(4, 6)}/${date.slice(0, 4)}`;

/**
 * Get the value of url param
 * @param param
 * @returns {*}
 */
export const $_GET = (param) => {
  const vars = {};
  window.location.href.replace(location.hash, "").replace(
    /[?&]+([^=&]+)=?([^&]*)?/gi, // regexp
    (m, key, value) => {
      // callback
      vars[key] = value !== undefined ? value : "";
    }
  );
  if (param) {
    return vars[param] ? vars[param] : null;
  }
  return vars;
};

export function removeZero(string) {
  return string.slice(8);
}

export const getHostName = () =>
  __DEV__ ? "https://dev.altima-assurances.fr" : window.location.origin;

export const fetchScript = (url, async = true) => {
  const script = document.createElement("script");
  script.src = url;
  script.async = async;
  document.body.appendChild(script);
};

export function fetchAltimaId(primaId) {
  const params = {
    system: "primav8",
    systemIds: primaId,
  };

  const query = Object.keys(params)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
    .join("&");

  const url =
    "/particuliers/auto/assurance-auto-usage/espace-client/api/on-demand/user-id?" +
    query;
  return fetch(url).then((promise) =>
    promise.json().then((response) => response.data[0].id)
  );
}

export function createDocumentTask(numClient, link, token) {
  return fetch(
    "/particuliers/auto/assurance-auto-usage/espace-client/pieces-justificatives/api/on-demand/task/add ",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        numClient: numClient,
        name: "PWYD : Nouveau fichier déposé",
        description: "Lien(s) vers le fichier :",
        urls: [link],
      }),
    }
  );
}
